type HelpBlock = {
  title?: string;
  body: string[];
  images?: string[];
};

export type HelpDoc = {
  id: string;
  title: string;
  keywords: string[];
  summary: string;
  blocks: HelpBlock[];
  fullUrl?: string;
  related?: {
    id: string;
    title: string;
    summary: string;
  }[];
};

export const helpDocs: HelpDoc[] = [
  {
    id: "대시보드",
    title: "대시보드",
    keywords: ["대시보드", "홈", "메인", "통계"],
    summary: "솔루션 전체 현황을 한눈에 확인하는 시작 화면",
    blocks: [
      {
        title: "현황 요약",
        images: ["/manual/Dashboard/Dashboard_1.png"],
        body: [
          "① 전체 프로젝트 수, 데이터셋 용량 등 핵심 지표 카드",
          "② 진행 중인 학습이 있는 경우 현재 학습 진행률 표시",
        ],
      },
      {
        title: "바로가기",
        images: ["/manual/Dashboard/Dashboard_2.png"],
        body: [
          "자주 사용하는 기능(신규 프로젝트 생성, 검사 시작, 리포트 확인 등)으로 즉시 이동할 수 있는 바로가기 버튼을 제공합니다.",
        ],
      },
    ],
  },

  {
    id: "공지사항",
    title: "공지사항",
    keywords: ["공지", "공지사항", "알림"],
    summary: "관리자가 등록한 공지사항을 확인하는 페이지",
    blocks: [
      {
        images: ["/manual/Notice/Notice_1.png"],
        body: [
          "시스템 점검, 업데이트 등 관리자가 등록한 공지 목록을 확인할 수 있습니다.",
          "(현재 준비 중인 화면으로, 추후 상세 내용이 추가될 예정입니다.)",
        ],
      },
    ],
  },

  {
    id: "프로젝트",
    title: "프로젝트 목록",
    keywords: ["프로젝트", "목록", "홈"],
    summary: "생성된 전체 프로젝트를 카드 형태로 확인하는 페이지",
    blocks: [
      {
        title: "프로젝트 목록",
        images: ["/manual/ProjectList/ProjectList_1.png"],
        body: [
          "① 검색/필터로 원하는 프로젝트를 찾을 수 있습니다.",
          "② 프로젝트 카드를 클릭하면 해당 프로젝트의 메인 화면으로 이동합니다.",
          "③ '신규 프로젝트' 버튼으로 새 프로젝트 생성을 시작할 수 있습니다.",
        ],
      },
    ],
    related: [
      {
        id: "프로젝트 생성",
        title: "프로젝트 생성",
        summary: "새 프로젝트를 만드는 과정입니다.",
      },
      {
        id: "프로젝트 화면",
        title: "프로젝트 화면",
        summary: "생성된 프로젝트의 메인 화면 구성입니다.",
      },
    ],
  },

  {
    id: "프로젝트 생성",
    title: "프로젝트 생성",
    keywords: ["프로젝트", "생성", "신규"],
    summary: "AI 프로젝트 생성 개요",
    blocks: [
      {
        body: [
          "AI 프로젝트 생성을 위해선 프로젝트 타입, 자재 이름, 데이터셋 등을 설정해야 합니다.",
          "아래 항목을 클릭해 프로젝트 타입별 생성 과정으로 이동할 수 있습니다.",
        ],
      },
    ],
    related: [
      {
        id: "프로젝트 생성 > 분류",
        title: "프로젝트 생성 - 분류",
        summary: "분류 프로젝트를 생성하는 과정입니다.",
      },
      {
        id: "프로젝트 생성 > 분석",
        title: "프로젝트 생성 - 분석",
        summary: "분석 프로젝트를 생성하는 과정입니다.",
      },
    ],
  },

  {
    id: "프로젝트 생성 > 분류",
    title: "프로젝트 생성 - 분류",
    keywords: ["프로젝트", "생성", "분류"],
    summary: "분류 프로젝트 생성 방법",
    blocks: [
      {
        title: "기본 정보 입력",
        images: ["/manual/CreateProject/CreateProject_1.png"],
        body: ["프로젝트 이름, 프로젝트 타입(분류), 자재 이름 등 기본 정보를 입력합니다."],
      },
      {
        title: "데이터셋 설정",
        images: ["/manual/CreateProject/CreateProject_2.png"],
        body: [
          "'새로 만들기'를 선택해 서버의 파일로 데이터셋을 새로 구성하거나, '기존 선택'으로 이미 등록된 데이터셋을 재사용할 수 있습니다.",
        ],
      },
      {
        title: "분류 클래스 수 설정",
        images: ["/manual/CreateProject/CreateProject_3.png"],
        body: ["분류할 클래스 개수를 설정한 뒤 생성을 완료하면 해당 프로젝트의 메인 화면으로 자동 이동합니다."],
      },
    ],
  },

  {
    id: "프로젝트 생성 > 분석",
    title: "프로젝트 생성 - 분석",
    keywords: ["프로젝트", "생성", "분석"],
    summary: "분석 프로젝트 생성 방법",
    blocks: [
      {
        title: "분석 타입 및 데이터셋 설정",
        images: ["/manual/CreateProject/CreateProject_4.png"],
        body: [
          "프로젝트 타입을 '분석'으로 선택하면, 기존에 생성된 프로젝트의 데이터셋을 그대로 가져와 재사용할 수 있습니다.",
        ],
      },
      {
        title: "사전분류 데이터 수정",
        images: ["/manual/CreateProject/CreateProject_5.png"],
        body: [
          "알고리즘이 자동으로 분류한 사전분류 결과를 확인하고, 필요한 경우 클래스 라벨을 직접 보정할 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "프로젝트 화면",
    title: "프로젝트 화면",
    keywords: ["프로젝트", "메인", "화면"],
    summary: "생성된 프로젝트의 메인 화면 구성 개요",
    blocks: [
      {
        body: [
          "프로젝트에 들어가면 상단 탭을 통해 분류/분석 작업, 학습 히스토리, 리포트, 설정 화면으로 이동할 수 있습니다.",
          "아래 항목을 클릭해 각 화면에 대한 설명으로 이동할 수 있습니다.",
        ],
      },
    ],
    related: [
      {
        id: "프로젝트 화면 > 분류",
        title: "프로젝트 화면 - 분류",
        summary: "이미지를 직접 라벨링하는 메인 작업 화면입니다.",
      },
      {
        id: "프로젝트 화면 > 분석",
        title: "프로젝트 화면 - 분석",
        summary: "학습 결과를 클래스별로 확인하는 화면입니다.",
      },
      {
        id: "프로젝트 화면 > 히스토리",
        title: "프로젝트 화면 - 히스토리",
        summary: "학습 이력과 회차별 정확도를 확인하는 화면입니다.",
      },
      {
        id: "프로젝트 화면 > 리포트",
        title: "프로젝트 화면 - 리포트",
        summary: "학습 결과를 리포트로 만드는 화면입니다.",
      },
      {
        id: "프로젝트 화면 > 설정",
        title: "프로젝트 화면 - 설정",
        summary: "프로젝트 정보, 데이터셋, 키, 클래스를 관리하는 화면입니다.",
      },
    ],
  },

  {
    id: "프로젝트 화면 > 분류",
    title: "프로젝트 화면 - 분류",
    keywords: ["프로젝트", "분류", "라벨링"],
    summary: "이미지 라벨링 메인 작업 화면",
    blocks: [
      {
        images: ["/manual/ProjectMain/ProjectMain_1.png"],
        body: [
          "① 프로젝트에 포함된 이미지 목록",
          "② 필터 on/off (키보드 tab)",
          "③ 필터 옵션 설정",
          "④ 라벨링 키 확인 및 클릭 라벨링",
          "⑤ 선택한 이미지의 최근 라벨링 기록 확인",
          "⑥ 라벨링 키 설정",
        ],
      },
    ],
  },

  {
    id: "프로젝트 화면 > 분석",
    title: "프로젝트 화면 - 분석",
    keywords: ["프로젝트", "분석", "클래스"],
    summary: "학습 결과를 클래스별로 확인하는 화면",
    blocks: [
      {
        images: ["/manual/ProjectMain/ProjectMain_2.png"],
        body: [
          "① 학습이 진행 중인 경우 상태를 실시간으로 확인할 수 있습니다.",
          "② 클래스별 이미지 개수와 정확도 등의 통계를 확인할 수 있습니다.",
          "③ 클래스를 클릭하면 해당 클래스로 분류된 이미지를 확인할 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "프로젝트 화면 > 히스토리",
    title: "프로젝트 화면 - 히스토리",
    keywords: ["프로젝트", "히스토리", "학습이력"],
    summary: "학습 이력과 회차별 정확도를 확인하는 화면",
    blocks: [
      {
        images: ["/manual/ProjectMain/ProjectMain_3.png"],
        body: [
          "① 해당 프로젝트의 전체 학습 이력을 확인할 수 있습니다.",
          "② 회차별 정확도 변화를 그래프로 확인할 수 있습니다.",
          "③ 특정 이력을 선택해 해당 모델을 기반으로 '이어 학습'을 진행할 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "프로젝트 화면 > 리포트",
    title: "프로젝트 화면 - 리포트",
    keywords: ["프로젝트", "리포트", "보고서"],
    summary: "학습 결과를 리포트로 만드는 화면",
    blocks: [
      {
        images: ["/manual/ProjectMain/ProjectMain_4.png"],
        body: [
          "① 템플릿을 선택하거나 빈 페이지에서 새로 시작할 수 있습니다.",
          "② 데이터 조회 기간을 설정하고 원하는 항목을 추가할 수 있습니다.",
          "③ 완성된 리포트는 PDF/Excel로 내보낼 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "프로젝트 화면 > 설정",
    title: "프로젝트 화면 - 설정",
    keywords: ["프로젝트", "설정", "키설정", "클래스관리"],
    summary: "프로젝트 정보, 데이터셋, 키, 클래스를 관리하는 화면",
    blocks: [
      {
        images: ["/manual/ProjectMain/ProjectMain_5.png"],
        body: [
          "① 프로젝트 이름, 자재명, 학습 옵션 등 기본 정보 수정",
          "② 데이터셋 파일 추가 및 수정",
          "③ 라벨링 키 설정",
          "④ 프로젝트 클래스 수정",
          "⑤ 그 외 기타 설정",
        ],
      },
    ],
  },

  {
    id: "검사관리",
    title: "검사관리",
    keywords: ["검사관리", "모델", "룰"],
    summary: "검사에 사용할 모델과 룰을 관리하는 메뉴 개요",
    blocks: [
      {
        body: [
          "실제 검사 현장에 적용할 AI 모델과 판정 룰(기준)을 등록하고 관리합니다.",
          "아래 항목을 클릭해 모델 관리, 룰 관리에 대한 설명으로 이동할 수 있습니다.",
        ],
      },
    ],
    related: [
      {
        id: "검사관리 > 모델",
        title: "검사관리 - 모델",
        summary: "검사에 사용할 AI 모델을 등록/관리합니다.",
      },
      {
        id: "검사관리 > 룰",
        title: "검사관리 - 룰",
        summary: "판정 기준이 되는 검사 룰을 등록/관리합니다.",
      },
    ],
  },

  {
    id: "검사관리 > 모델",
    title: "검사관리 - 모델",
    keywords: ["검사관리", "모델", "새모델"],
    summary: "검사에 사용할 AI 모델을 등록/관리하는 화면",
    blocks: [
      {
        images: ["/manual/Inspection/Inspection_1.png"],
        body: [
          "① 등록된 모델 목록과 각 모델의 상태를 확인할 수 있습니다.",
          "② '새 모델' 버튼을 눌러 학습이 완료된 프로젝트를 기반으로 검사 모델을 새로 등록할 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "검사관리 > 룰",
    title: "검사관리 - 룰",
    keywords: ["검사관리", "룰", "판정기준"],
    summary: "판정 기준이 되는 검사 룰을 등록/관리하는 화면",
    blocks: [
      {
        images: ["/manual/Inspection/Inspection_2.png"],
        body: [
          "① 등록된 검사 룰 목록을 확인할 수 있습니다.",
          "② 클래스 조합, 임계값 등을 설정해 새로운 룰을 생성할 수 있습니다.",
        ],
      },
    ],
  },

  {
    id: "검사현황",
    title: "검사현황",
    keywords: ["검사현황", "검사결과", "overkill"],
    summary: "실제 검사가 진행된 결과를 조회하는 화면",
    blocks: [
      {
        images: ["/manual/InspectionStatus/InspectionStatus_1.png"],
        body: [
          "① 조회 기간, 이름 등으로 검사 이력을 검색할 수 있습니다.",
          "② 검사된 이미지와 판정 결과(양품/불량, overkill 등)를 목록에서 확인할 수 있습니다.",
          "③ 조건에 맞는 검사 이미지가 없는 경우 안내 문구가 표시됩니다.",
        ],
      },
    ],
  },

  {
    id: "환경설정",
    title: "환경설정",
    keywords: ["설정", "환경설정", "사용자설정"],
    summary: "솔루션 및 사용자 계정 설정 화면",
    blocks: [
      {
        title: "사이트 설정",
        images: ["/manual/Setting/Setting_1.png"],
        body: ["테마, 알림 등 솔루션 전반의 환경을 설정할 수 있습니다."],
      },
      {
        title: "사용자 설정",
        images: ["/manual/Setting/Setting_2.png"],
        body: ["이름, 연락처, 태그 등 사용자 개인 정보를 수정할 수 있습니다."],
      },
    ],
  },
];
